import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const MAX_FILES = 5;
const MAX_SIZE = 5 * 1024 * 1024;
const EXTS = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "pdf", "txt", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "mp4", "mov", "webm"];
const ACCEPT = EXTS.map((e) => `.${e}`).join(",") + ",image/*,video/mp4,video/quicktime,video/webm";

export const fileIcon = (name = "", type = "") => {
  if (type.startsWith("image/")) return "🖼️";
  if (type.startsWith("video/")) return "🎬";
  if (/\.pdf$/i.test(name)) return "📕";
  if (/\.(xls|xlsx)$/i.test(name)) return "📊";
  if (/\.(ppt|pptx)$/i.test(name)) return "📽️";
  return "📄";
};
export const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const extOf = (n) => (n.includes(".") ? n.split(".").pop().toLowerCase() : "");

const PendingItem = ({ item, onRemove }) => (
  <div className="flex items-center gap-3 p-2 rounded-2xl bg-arc" data-testid={`attach-item-${item.key}`}>
    {item.preview ? <img src={item.preview} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-2xl">{fileIcon(item.file.name, item.file.type)}</div>}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-arc-text truncate">{item.file.name}</div>
      <div className="text-xs text-arc-muted">{fmtSize(item.file.size)}</div>
    </div>
    <button type="button" onClick={() => onRemove(item.key)} data-testid={`attach-remove-${item.key}`} className="text-arc-accent font-bold text-xs hover:underline px-2">Remover</button>
  </div>
);

export const ChallengeSubmitDialog = ({ challenge, open, onClose, onSent }) => {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [items, setItems] = useState([]);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  if (!challenge) return null;
  const previous = challenge.attachments || [];
  const slots = MAX_FILES - previous.length - items.length;

  const addFiles = (list) => {
    setError("");
    const incoming = Array.from(list || []);
    const errs = [];
    const ok = [];
    incoming.forEach((f) => {
      if (!EXTS.includes(extOf(f.name))) errs.push(`${f.name}: tipo não permitido`);
      else if (f.size > MAX_SIZE) errs.push(`${f.name}: maior que 5MB`);
      else ok.push(f);
    });
    if (ok.length > slots) errs.push(`Limite de ${MAX_FILES} arquivos por desafio`);
    const accepted = ok.slice(0, Math.max(0, slots)).map((f) => ({
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f,
      preview: f.type.startsWith("image/") && !/heic|heif/i.test(f.type) ? URL.createObjectURL(f) : null,
    }));
    setItems((cur) => [...cur, ...accepted]);
    if (errs.length) setError(errs.join(" · "));
  };
  const remove = (key) => setItems((cur) => cur.filter((i) => { if (i.key === key && i.preview) URL.revokeObjectURL(i.preview); return i.key !== key; }));
  const close = () => { items.forEach((i) => i.preview && URL.revokeObjectURL(i.preview)); setItems([]); setComment(""); setError(""); setProgress(null); onClose(); };

  const send = async () => {
    setError(""); setProgress(0);
    const fd = new FormData();
    items.forEach((i) => fd.append("files", i.file, i.file.name));
    if (comment.trim()) fd.append("comment", comment.trim());
    try {
      await axios.post(`${API}/student/challenges/${challenge.id}/submit`, fd, { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)) });
      close(); onSent();
    } catch (err) { setError(err?.response?.data?.detail || "Erro ao enviar. Tente novamente."); setProgress(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && progress === null && close()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl" data-testid="challenge-submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-fredoka text-2xl">{challenge.title}</DialogTitle>
          <DialogDescription>Envie fotos ou arquivos que comprovem o desafio (opcional). Até {MAX_FILES} arquivos de 5MB.</DialogDescription>
        </DialogHeader>
        {previous.length > 0 && (
          <div data-testid="attach-previous">
            <div className="text-xs font-semibold text-arc-muted uppercase mb-1">Já enviados ({previous.length})</div>
            <div className="space-y-1">
              {previous.map((a) => <div key={a.id} className="text-sm text-arc-text truncate">{fileIcon(a.filename, a.content_type)} {a.filename}</div>)}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="attach-camera-input" />
          <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="attach-file-input" />
          <button type="button" disabled={slots <= 0 || progress !== null} onClick={() => camRef.current.click()} className="arc-btn arc-btn-secondary py-3 text-sm disabled:opacity-40 disabled:grayscale disabled:cursor-not-allowed" data-testid="attach-camera-btn">📷 Tirar foto</button>
          <button type="button" disabled={slots <= 0 || progress !== null} onClick={() => fileRef.current.click()} className="arc-btn arc-btn-outline py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="attach-file-btn">📎 Escolher arquivos</button>
        </div>
        <div className="text-xs text-arc-muted">Fotos, vídeos curtos, PDF, Word, Excel, PowerPoint ou TXT · {Math.max(0, slots)} espaço(s) restante(s)</div>
        <div className="space-y-2" data-testid="attach-list">
          {items.map((i) => <PendingItem key={i.key} item={i} onRemove={remove} />)}
        </div>
        <div>
          <label className="text-xs font-semibold text-arc-muted uppercase">Comentário (opcional)</label>
          <textarea rows={3} maxLength={1000} className="arc-input mt-1" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Conte para o professor como foi..." data-testid="attach-comment" />
        </div>
        {error && <div className="p-3 rounded-2xl bg-pink-100 text-arc-accent text-sm font-semibold" data-testid="attach-error">{error}</div>}
        {progress !== null && (
          <div className="h-2 rounded-full bg-arc overflow-hidden" data-testid="attach-progress">
            <div className="h-full bg-arc-primary transition-[width]" style={{ width: `${progress}%`, background: "var(--arc-primary)" }} />
          </div>
        )}
        <button type="button" onClick={send} disabled={progress !== null} className="arc-btn arc-btn-primary w-full disabled:opacity-60" data-testid="attach-send-btn">
          {progress !== null ? `Enviando... ${progress}%` : `Enviar desafio${items.length ? ` com ${items.length} anexo(s)` : ""}`}
        </button>
      </DialogContent>
    </Dialog>
  );
};
